/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import useChatSocket from '@/lib/useChatSocket';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Send, Users, WifiOff } from 'lucide-react';
import { CATEGORY_META, initials, extractYoutubeId } from '@/lib/utils-cat';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

// Polling used as a safety fallback if WebSocket is not connected.
const POLL_MS_FALLBACK = 8000;
const POLL_MS_WS = 25000; // gentle sync when live

const ChatConversationPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const pollingRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          api.get(`/chat/conversations/${id}`),
          api.get(`/chat/conversations/${id}/messages`),
        ]);
        setConvo(cRes.data);
        setMessages(mRes.data);
        if (mRes.data.length) lastTsRef.current = mRes.data[mRes.data.length - 1].created_at;
        setTimeout(scrollToBottom, 50);
      } catch (e) {
        toast.error(t('errorOccurred'));
        navigate('/chat');
      }
    };
    init();
    // eslint-disable-next-line
  }, [id]);

  // Handle live events from the WebSocket
  const handleWsEvent = (evt) => {
    if (!evt || !evt.type) return;
    if (evt.type === 'message' && evt.data) {
      const msg = evt.data;
      setMessages(prev => {
        if (prev.find(m => m.id === msg.id)) return prev;
        const merged = [...prev, msg];
        lastTsRef.current = msg.created_at;
        return merged;
      });
      setTimeout(scrollToBottom, 50);
    } else if (evt.type === 'message_deleted' && evt.message_id) {
      setMessages(prev => prev.filter(m => m.id !== evt.message_id));
    }
  };

  const { connected: wsConnected } = useChatSocket(convo ? id : null, handleWsEvent);

  useEffect(() => {
    if (!convo) return;
    const poll = async () => {
      try {
        const params = lastTsRef.current ? `?since=${encodeURIComponent(lastTsRef.current)}` : '';
        const res = await api.get(`/chat/conversations/${id}/messages${params}`);
        if (res.data && res.data.length) {
          setMessages(prev => {
            const existing = new Set(prev.map(m => m.id));
            const news = res.data.filter(m => !existing.has(m.id));
            if (news.length) {
              const merged = [...prev, ...news];
              lastTsRef.current = merged[merged.length - 1].created_at;
              return merged;
            }
            return prev;
          });
          setTimeout(scrollToBottom, 50);
        }
      } catch (err) {
        console.error('ChatConversation: poll fetch failed', err);
      }
    };
    // If WS is connected, use a much larger poll interval (safety net).
    const interval = wsConnected ? POLL_MS_WS : POLL_MS_FALLBACK;
    pollingRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') poll();
    }, interval);
    return () => clearInterval(pollingRef.current);
  }, [convo, id, wsConnected]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/chat/conversations/${id}/messages`, { content: text.trim() });
      setMessages(prev => {
        if (prev.find(m => m.id === res.data.id)) return prev;
        const merged = [...prev, res.data];
        lastTsRef.current = res.data.created_at;
        return merged;
      });
      setText('');
      setTimeout(scrollToBottom, 50);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!convo) return <div className="text-sm text-muted-foreground py-8">{t('loading')}</div>;

  const title = convo.type === 'group' ? convo.title : convo.other_user?.full_name || '';
  const cat = convo.type === 'group' ? CATEGORY_META[convo.category] : CATEGORY_META[convo.other_user?.category];
  const HeaderIcon = convo.type === 'group' ? (cat?.icon || Users) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] lg:h-[calc(100vh-8rem)] bg-white border border-border rounded-2xl overflow-hidden" data-testid="chat-conversation">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} data-testid="chat-back-button">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {convo.type === 'group' ? (
          <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>
            <HeaderIcon className="h-4 w-4" />
          </div>
        ) : (
          <Avatar className="h-9 w-9">
            {convo.other_user?.avatar_base64 && <AvatarImage src={convo.other_user.avatar_base64} />}
            <AvatarFallback style={{ backgroundColor: cat?.soft, color: cat?.textOnSoft }}>{initials(convo.other_user?.full_name)}</AvatarFallback>
          </Avatar>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate flex items-center gap-1.5">
            {title}
            {wsConnected ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600 dark:text-green-400"
                title="Real-time connesso"
                data-testid="chat-ws-live"
              >
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                live
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground"
                title="Modalità polling"
                data-testid="chat-ws-offline"
              >
                <WifiOff className="h-2.5 w-2.5" />
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {convo.type === 'group' ? t(cat?.label || '') : convo.other_user?.specialty || t(cat?.label || '')}
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2 bg-secondary/30">
        {messages.map((m) => {
          const isMine = m.author?.id === user?.id;
          const authorCat = CATEGORY_META[m.author?.category];
          const ytId = m.youtube_id || extractYoutubeId(m.content);
          return (
            <div key={m.id} className={`flex gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
              {!isMine && (
                <Avatar className="h-7 w-7 mt-0.5">
                  {m.author?.avatar_base64 && <AvatarImage src={m.author.avatar_base64} />}
                  <AvatarFallback className="text-[10px]" style={{ backgroundColor: authorCat?.soft, color: authorCat?.textOnSoft }}>
                    {initials(m.author?.full_name)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${isMine ? 'bg-primary text-primary-foreground rounded-tr-md' : 'bg-white border border-border rounded-tl-md'}`}>
                {!isMine && (
                  <div className="text-[10px] font-semibold mb-0.5 opacity-80">{m.author?.full_name}</div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                {ytId && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe title="yt" className="w-full h-full" src={`https://www.youtube.com/embed/${ytId}`} allowFullScreen />
                  </div>
                )}
                {m.image_base64 && (
                  <img src={m.image_base64} alt="attachment" className="mt-2 rounded-lg max-w-full" />
                )}
              </div>
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-8">{t('noConversations')}</div>
        )}
      </div>

      <div className="border-t border-border p-2 flex items-end gap-2 bg-white">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('typeMessage')}
          rows={1}
          className="resize-none min-h-[40px] max-h-[120px]"
          data-testid="chat-composer-input"
        />
        <Button size="icon" onClick={send} disabled={sending || !text.trim()} data-testid="chat-composer-send-button">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ChatConversationPage;
